import { create } from "zustand";

/*
 * Store de sessão (substrato da Decisão D — ADR 0026).
 *
 * INVARIANTES DURAS (gate do FE-0 — ver ADR 0026 §Invariantes):
 *  - I1 (zero persistência): access e refresh vivem EXCLUSIVAMENTE em memória
 *    (heap, dentro do estado Zustand). Nenhum mecanismo de armazenamento do
 *    browser, nenhum cookie, nenhum middleware de persistência do zustand.
 *  - I3 (sem rehidratação): nenhum canal de sync cross-aba, nenhum listener de
 *    evento de armazenamento, nenhuma rehidratação por cache de navegação.
 *    F5 = store novo, vazio = deslogado (custo aceito no ADR).
 *
 * O interceptor do CP-C lê e escreve este store FORA do React via
 * `useSessaoStore.getState()` — ver getters de conveniência abaixo.
 */

/** Permissão de uma ação, conforme o RBAC discovery de `GET /api/auth/eu`. */
export interface PermissaoUsuario {
  acao: string;
  irrestrito: boolean;
  empresas: string[];
}

/** Dados do usuário autenticado, vindos de `GET /api/auth/eu`. */
export interface UsuarioAutenticado {
  id: string;
  email: string;
  nome: string;
  permissoes: PermissaoUsuario[];
}

/**
 * Par de tokens devolvido por `POST /api/auth/login` e `POST /api/auth/refresh`.
 *
 * Shape do WIRE EXATO do backend (snake_case, `backend/src/flow/api/auth.py:76`):
 * `access_token`, `refresh_token`, `expires_in`. O HTTP client (CP-C) consome
 * este shape direto do JSON e o entrega a `definirSessao`, que faz o boundary
 * `expires_in → expiraEm` — o estado INTERNO do store fica em camelCase.
 */
export interface ParDeTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface EstadoSessao {
  /** Token de acesso (Bearer). Nulo quando deslogado. */
  accessToken: string | null;
  /** Token de refresh (rotação por família, single-use). Nulo quando deslogado. */
  refreshToken: string | null;
  /** TTL em segundos do access (do `expires_in` do contrato). Nulo quando deslogado. */
  expiraEm: number | null;
  /** Dados do `/eu`. Nulo enquanto não carregados. */
  usuario: UsuarioAutenticado | null;

  /** Grava o par de tokens + TTL (após login ou refresh). */
  definirSessao: (par: ParDeTokens) => void;
  /** Grava os dados do usuário autenticado (após `GET /api/auth/eu`). */
  definirUsuario: (usuario: UsuarioAutenticado) => void;
  /** Zera TUDO (logout, 401 no refresh, reuse-detection). */
  limpar: () => void;
}

const ESTADO_DESLOGADO = {
  accessToken: null,
  refreshToken: null,
  expiraEm: null,
  usuario: null,
} as const;

export const useSessaoStore = create<EstadoSessao>((set) => ({
  ...ESTADO_DESLOGADO,

  definirSessao: (par) =>
    // Boundary do mapeamento snake_case (wire) → camelCase (estado interno):
    // `expires_in` (segundos do contrato) vira `expiraEm`.
    set({
      accessToken: par.access_token,
      refreshToken: par.refresh_token,
      expiraEm: par.expires_in,
    }),

  definirUsuario: (usuario) => set({ usuario }),

  limpar: () => set({ ...ESTADO_DESLOGADO }),
}));

/*
 * Getters de conveniência fora-de-React.
 *
 * O interceptor do CP-C precisa LER o access (request) e LER/trocar o par
 * (response 401 single-flight) sem um hook React. Estes helpers encapsulam o
 * `useSessaoStore.getState()` para o CP-C — que continua sendo a API estável de
 * acesso fora-de-React.
 */

/** Lê o access token atual (ou null). Usado pelo interceptor no request. */
export const obterAccessToken = (): string | null =>
  useSessaoStore.getState().accessToken;

/** Lê o refresh token atual (ou null). Usado pelo interceptor no refresh. */
export const obterRefreshToken = (): string | null =>
  useSessaoStore.getState().refreshToken;

/** Há sessão ativa? (existe access token). */
export const estaAutenticado = (): boolean =>
  useSessaoStore.getState().accessToken !== null;
